import {
  Autocomplete,
  TextField,
  Box,
  Button,
  Skeleton,
  Alert,
  IconButton,
  CircularProgress,
  Link,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import { usePartyManager } from './PartyManagerContext';
import {
  POSSIBLE_SOURCES,
  EDIT_SOURCE,
  CODEMETA_SOURCE,
} from './partyManagerUtils';

const ParentPartySelector = ({ label, options }) => {
  const {
    partyData,
    isUIBlocked,
    handleFieldChange,
    handleCycleSource,
    getAvailableSources,
    isPartyManagerLoading,
    parentPartyMissingSources,
    isCheckingParentParty,
    checkParentPartySources,
  } = usePartyManager();

  const fieldKey = 'parentPartyId';
  const fieldState = partyData[fieldKey];

  if (!fieldState) {
    return null;
  }

  const availableSources = getAvailableSources(fieldKey);
  const selectedId =
    fieldState.selected === 'edit'
      ? fieldState.edit
      : fieldState[fieldState.selected];
  const selectedOption = options.find((p) => p._id === selectedId) || null;

  const getButtonIcon = () => {
    const selectedSource = fieldState.selected;
    if (selectedSource === 'edit') return EDIT_SOURCE.icon;
    if (selectedSource === 'codemeta') {
      return (
        <img
          src={CODEMETA_SOURCE.icon}
          alt={CODEMETA_SOURCE.name}
          style={{ width: '24px', height: '24px' }}
        />
      );
    }
    const sourceInfo = POSSIBLE_SOURCES[selectedSource];
    if (sourceInfo?.icon) {
      return (
        <img
          src={sourceInfo.icon}
          alt={sourceInfo.name}
          style={{ width: '24px', height: '24px' }}
        />
      );
    }
    return '?';
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 0.5,
          flexWrap: 'nowrap',
        }}
      >
        {isPartyManagerLoading ? (
          <Skeleton variant="rounded" width="100%" height={40} />
        ) : (
          <>
            <Autocomplete
              id="parent-party-select"
              fullWidth
              options={options}
              size="small"
              getOptionLabel={(option) => {
                if (!option) return '';
                const number = option.customerNumber || option.supplierNumber;
                const numberLabel = number ? `[${number}] ` : '';
                const nameLabel = option.company
                  ? option.company
                  : `${option.firstName ?? '--'} ${option.lastName ?? '--'}`.trim();
                return `${numberLabel}${nameLabel}`;
              }}
              renderOption={(props, option) => {
                const number = option.customerNumber || option.supplierNumber;
                const numberLabel = number ? `[${number}] ` : '';
                const nameLabel = option.company
                  ? option.company
                  : `${option.firstName ?? '--'} ${option.lastName ?? '--'}`;
                return (
                  <li {...props} key={option._id}>
                    {numberLabel}
                    {nameLabel}
                  </li>
                );
              }}
              value={selectedOption}
              onChange={(event, newValue) => {
                handleFieldChange(fieldKey, newValue ? newValue._id : null);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={label}
                  sx={{
                    '& .MuiInputBase-input': {
                      color: 'rgba(0, 0, 0, 0.87)',
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(0, 0, 0, 0.38)',
                      '&.MuiInputLabel-shrink': {
                        color: 'rgba(0, 0, 0, 0.87)',
                      },
                    },
                  }}
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      inputProps: {
                        ...params.inputProps,
                        autoComplete: 'nope',
                      },
                    },
                  }}
                />
              )}
              disabled={isUIBlocked}
              sx={{ flex: 1, minWidth: 0 }}
            />
            {availableSources.length > 1 && (
              <Button
                title={`Quelle wechseln: Aktuell ${fieldState.selected}.`}
                sx={{
                  width: '40px',
                  height: '40px',
                  minWidth: '40px',
                  maxWidth: '40px',
                  flexShrink: 0,
                }}
                onClick={() => handleCycleSource(fieldKey)}
                variant="outlined"
              >
                {getButtonIcon()}
              </Button>
            )}
          </>
        )}
      </Box>

      {selectedId &&
        (isCheckingParentParty || parentPartyMissingSources.length > 0) && (
          <Box sx={{ mt: 1, minHeight: '38px' }}>
            {isCheckingParentParty ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  color: 'text.secondary',
                  p: 1,
                }}
              >
                <CircularProgress size={20} />
                <Typography variant="body2">
                  Prüfe Hauptfirma in Zielsystemen...
                </Typography>
              </Box>
            ) : (
              parentPartyMissingSources.length > 0 && (
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{
                    display: 'flex',
                    flexWrap: 'nowrap',
                    width: '100%',
                    py: 0,
                    px: 1,
                    '& .MuiAlert-message': {
                      display: 'flex',
                      width: '100%',
                      flexDirection: 'row',
                      alignItems: 'center',
                    },
                  }}
                >
                  <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                    Hauptfirma nicht gefunden in:&nbsp;
                    <strong>{parentPartyMissingSources.join(', ')}</strong>
                  </Typography>

                  <Link
                    href={`/crm/parties/${selectedId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Hauptfirma in neuem Tab bearbeiten"
                  >
                    <IconButton size="small" title="Hauptfirma bearbeiten">
                      <OpenInNewOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Link>

                  <IconButton
                    size="small"
                    onClick={() => checkParentPartySources(selectedId)}
                    title="Prüfung erneut ausführen"
                    aria-label="Prüfung erneut ausführen"
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Alert>
              )
            )}
          </Box>
        )}
    </Box>
  );
};

export default ParentPartySelector;
